import React, { useState, useRef, useEffect } from 'react';
import { Link } from 'react-router-dom';
import {
  Briefcase, Search, ChevronRight, MapPin, User,
  Building2, Calendar, TrendingUp, ChevronDown, X, Check
} from 'lucide-react';
import {
  getFilteredAffaires, getClientById, getIngenieurById, getSocieteById,
  statutAffaireLabels, ingenieurs, getFilteredClients
} from '../data/mockData';
import { useFilters } from '../App';

// ==========================================
// Composant MultiSelect réutilisable
// ==========================================
const MultiSelect = ({ label, icon: Icon, options, selected, onChange, renderOption }) => {
  const [isOpen, setIsOpen] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (ref.current && !ref.current.contains(e.target)) setIsOpen(false);
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const toggleOption = (value) => {
    if (selected.includes(value)) {
      onChange(selected.filter(v => v !== value));
    } else {
      onChange([...selected, value]);
    }
  };

  const clearAll = (e) => {
    e.stopPropagation();
    onChange([]);
  };

  const displayLabel = selected.length === 0
    ? label
    : selected.length === 1
      ? options.find(o => o.value === selected[0])?.label || label
      : `${selected.length} sélectionnés`;

  return (
    <div ref={ref} style={{ position: 'relative', minWidth: '180px' }}>
      <button
        onClick={() => setIsOpen(!isOpen)}
        style={{
          display: 'flex', alignItems: 'center', gap: '0.5rem',
          padding: '0.625rem 1rem', width: '100%',
          border: selected.length > 0 ? '2px solid var(--primary)' : '1px solid var(--gray-300)',
          borderRadius: 'var(--radius)', background: selected.length > 0 ? 'var(--primary-50)' : 'white',
          fontSize: '0.875rem', fontFamily: 'var(--font-sans)', cursor: 'pointer',
          color: selected.length > 0 ? 'var(--primary)' : 'var(--gray-700)',
          textAlign: 'left', transition: 'all 0.2s'
        }}
      >
        {Icon && <Icon size={16} style={{ color: selected.length > 0 ? 'var(--primary)' : 'var(--gray-400)', flexShrink: 0 }} />}
        <span style={{ flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
          {displayLabel}
        </span>
        {selected.length > 0 && (
          <span
            onClick={clearAll}
            style={{
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              width: '18px', height: '18px', borderRadius: '50%',
              background: 'var(--primary)', color: 'white', flexShrink: 0, cursor: 'pointer'
            }}
          >
            <X size={10} />
          </span>
        )}
        <ChevronDown size={16} style={{
          color: 'var(--gray-400)', flexShrink: 0,
          transform: isOpen ? 'rotate(180deg)' : 'none', transition: 'transform 0.2s'
        }} />
      </button>

      {isOpen && (
        <div style={{
          position: 'absolute', top: 'calc(100% + 4px)', left: 0, right: 0,
          background: 'white', border: '1px solid var(--gray-200)',
          borderRadius: 'var(--radius)', boxShadow: 'var(--shadow-lg)',
          zIndex: 100, maxHeight: '280px', overflowY: 'auto',
          minWidth: '220px'
        }}>
          <div style={{
            padding: '0.5rem 0.75rem', borderBottom: '1px solid var(--gray-200)',
            display: 'flex', justifyContent: 'space-between', alignItems: 'center'
          }}>
            <span style={{ fontSize: '0.75rem', fontWeight: 600, color: 'var(--gray-500)', textTransform: 'uppercase' }}>
              {selected.length} / {options.length}
            </span>
            <button
              onClick={(e) => { e.stopPropagation(); onChange(selected.length === options.length ? [] : options.map(o => o.value)); }}
              style={{
                background: 'none', border: 'none', cursor: 'pointer',
                fontSize: '0.75rem', color: 'var(--primary)', fontWeight: 500, fontFamily: 'var(--font-sans)'
              }}
            >
              {selected.length === options.length ? 'Tout désélectionner' : 'Tout sélectionner'}
            </button>
          </div>

          {options.map(option => {
            const isSelected = selected.includes(option.value);
            return (
              <div
                key={option.value}
                onClick={(e) => { e.stopPropagation(); toggleOption(option.value); }}
                style={{
                  display: 'flex', alignItems: 'center', gap: '0.625rem',
                  padding: '0.5rem 0.75rem', cursor: 'pointer',
                  background: isSelected ? 'var(--primary-50)' : 'transparent',
                  transition: 'background 0.15s'
                }}
                onMouseEnter={(e) => { if (!isSelected) e.currentTarget.style.background = 'var(--gray-50)'; }}
                onMouseLeave={(e) => { if (!isSelected) e.currentTarget.style.background = 'transparent'; }}
              >
                <div style={{
                  width: '18px', height: '18px', borderRadius: '4px',
                  border: isSelected ? '2px solid var(--primary)' : '2px solid var(--gray-300)',
                  background: isSelected ? 'var(--primary)' : 'white',
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  flexShrink: 0, transition: 'all 0.15s'
                }}>
                  {isSelected && <Check size={12} style={{ color: 'white' }} />}
                </div>
                <div style={{ flex: 1, minWidth: 0 }}>
                  {renderOption ? renderOption(option) : (
                    <span style={{ fontSize: '0.875rem', color: 'var(--gray-900)' }}>{option.label}</span>
                  )}
                </div>
              </div>
            );
          })}

          {options.length === 0 && (
            <div style={{ padding: '1rem', textAlign: 'center', color: 'var(--gray-400)', fontSize: '0.8125rem' }}>
              Aucune option
            </div>
          )}
        </div>
      )}
    </div>
  );
};

// ==========================================
// Page liste des affaires
// ==========================================
const AffairesList = () => {
  const filters = useFilters();
  const [search, setSearch] = useState('');
  const [filterStatut, setFilterStatut] = useState('tous');
  const [filterIngenieurs, setFilterIngenieurs] = useState([]);
  const [filterClients, setFilterClients] = useState([]);
  const [filterVilles, setFilterVilles] = useState([]);

  const allAffaires = getFilteredAffaires(filters);
  const allClients = getFilteredClients(filters);

  const formatMontant = (montant) => {
    return new Intl.NumberFormat('fr-FR', { style: 'currency', currency: 'EUR', minimumFractionDigits: 0 }).format(montant);
  };

  // Options pour les multi-selects
  const clientOptions = allClients
    .filter(c => allAffaires.some(a => a.clientId === c.id))
    .map(c => ({ value: c.id, label: c.nom, type: c.marchePublic ? 'public' : 'privé' }));

  const villeOptions = [...new Set(allAffaires.map(a => a.ville))]
    .sort()
    .map(v => ({ value: v, label: v }));

  const filteredIngs = filters.agenceId
    ? ingenieurs.filter(i => i.agenceId === filters.agenceId)
    : ingenieurs;

  const ingenieurOptions = filteredIngs
    .filter(i => allAffaires.some(a => a.ingenieurId === i.id))
    .map(i => ({ value: i.id, label: `${i.prenom} ${i.nom}`, initiales: i.initiales, couleur: i.couleur, role: i.role }));

  // Filtrage
  const filteredAffaires = allAffaires.filter(a => {
    const client = getClientById(a.clientId);
    const matchSearch = search === '' ||
      a.numero.toLowerCase().includes(search.toLowerCase()) ||
      a.designation.toLowerCase().includes(search.toLowerCase()) ||
      client?.nom.toLowerCase().includes(search.toLowerCase()) ||
      a.adresse.toLowerCase().includes(search.toLowerCase());
    const matchStatut = filterStatut === 'tous' || a.statut === filterStatut;
    const matchIngenieur = filterIngenieurs.length === 0 || filterIngenieurs.includes(a.ingenieurId);
    const matchClient = filterClients.length === 0 || filterClients.includes(a.clientId);
    const matchVille = filterVilles.length === 0 || filterVilles.includes(a.ville);
    return matchSearch && matchStatut && matchIngenieur && matchClient && matchVille;
  });

  const activeFiltersCount = (filterIngenieurs.length > 0 ? 1 : 0) + (filterClients.length > 0 ? 1 : 0) + (filterVilles.length > 0 ? 1 : 0) + (filterStatut !== 'tous' ? 1 : 0);

  const clearAllFilters = () => {
    setFilterStatut('tous');
    setFilterIngenieurs([]);
    setFilterClients([]);
    setFilterVilles([]);
    setSearch('');
  };

  const renderAffaireRow = (a) => {
    const client = getClientById(a.clientId);
    const ingenieur = getIngenieurById(a.ingenieurId);
    const societe = getSocieteById(a.societeId);
    const statut = statutAffaireLabels[a.statut];

    return (
      <Link to={`/affaires/${a.id}`} key={a.id} style={{ textDecoration: 'none', color: 'inherit' }}>
        <div className="offre-card" style={{ marginBottom: '0.5rem' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '1rem' }}>
            <div style={{ flex: '0 0 auto' }}>
              <div className="user-avatar user-avatar-sm" style={{ background: ingenieur?.couleur || '#6B7280' }}>
                {ingenieur?.initiales}
              </div>
            </div>
            <div style={{ flex: 1, minWidth: 0 }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginBottom: '0.25rem', flexWrap: 'wrap' }}>
                <span className="offre-reference">{a.numero}</span>
                <span className="statut-badge" style={{ background: statut.bg, color: statut.color }}>
                  {statut.label}
                </span>
                <span className="badge badge-secondary">{a.codeMission}</span>
                <span className="badge" style={{
                  background: societe?.id === 1 ? '#DBEAFE' : '#FFF7ED',
                  color: societe?.id === 1 ? '#1E40AF' : '#9A3412',
                  fontSize: '0.625rem'
                }}>
                  {societe?.nom}
                </span>
              </div>
              <h3 style={{ margin: 0, fontSize: '0.9375rem', fontWeight: 600 }}>{a.designation}</h3>
              <div style={{ display: 'flex', gap: '1rem', marginTop: '0.25rem', fontSize: '0.8125rem', color: 'var(--gray-500)' }}>
                <span style={{ display: 'flex', alignItems: 'center', gap: '0.25rem' }}>
                  <User size={12} /> {client?.nom}
                </span>
                <span style={{ display: 'flex', alignItems: 'center', gap: '0.25rem' }}>
                  <MapPin size={12} /> {a.ville}
                </span>
                <span style={{ display: 'flex', alignItems: 'center', gap: '0.25rem' }}>
                  <Calendar size={12} /> {new Date(a.dateCreation).toLocaleDateString('fr-FR')}
                </span>
              </div>
            </div>
            <div style={{ textAlign: 'right' }}>
              <div style={{ fontWeight: 700, color: 'var(--primary)', fontSize: '1.0625rem' }}>{formatMontant(a.montantHT)}</div>
              <div style={{ fontSize: '0.75rem', color: 'var(--gray-500)' }}>Avancement : {a.avancement}%</div>
              <div className="progress-bar" style={{ width: '100px', height: '4px', marginTop: '0.25rem' }}>
                <div className="progress-fill" style={{ width: `${a.avancement}%` }}></div>
              </div>
            </div>
            <ChevronRight size={18} style={{ color: 'var(--gray-400)' }} />
          </div>
        </div>
      </Link>
    );
  };

  return (
    <div className="page-container">
      <div className="page-header">
        <div>
          <h1><Briefcase size={28} /> Affaires</h1>
          <div className="page-meta">
            <span>{allAffaires.length} affaires au total</span>
            <span>{allAffaires.filter(a => a.statut === 'en_cours').length} en cours</span>
          </div>
        </div>
      </div>

      {/* Barre de recherche */}
      <div className="filters-bar">
        <div className="search-input-group">
          <Search size={18} className="search-icon" />
          <input
            type="text"
            className="search-input"
            placeholder="Rechercher par numéro, désignation, client, adresse..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
        </div>
      </div>

      {/* Filtres multi-select */}
      <div style={{ display: 'flex', gap: '0.75rem', marginBottom: '1.5rem', flexWrap: 'wrap', alignItems: 'center' }}>
        <select
          className="filter-select"
          value={filterStatut}
          onChange={(e) => setFilterStatut(e.target.value)}
          style={{
            border: filterStatut !== 'tous' ? '2px solid var(--primary)' : undefined,
            background: filterStatut !== 'tous' ? 'var(--primary-50)' : undefined,
            color: filterStatut !== 'tous' ? 'var(--primary)' : undefined,
          }}
        >
          <option value="tous">Tous les statuts</option>
          <option value="en_cours">En cours</option>
          <option value="terminee">Terminée</option>
        </select>

        <MultiSelect
          label="Tous les clients"
          icon={Building2}
          options={clientOptions}
          selected={filterClients}
          onChange={setFilterClients}
          renderOption={(option) => (
            <div>
              <span style={{ fontSize: '0.875rem', color: 'var(--gray-900)', display: 'block' }}>{option.label}</span>
              <span style={{ fontSize: '0.6875rem', color: 'var(--gray-500)' }}>{option.type}</span>
            </div>
          )}
        />

        <MultiSelect
          label="Toutes les villes"
          icon={MapPin}
          options={villeOptions}
          selected={filterVilles}
          onChange={setFilterVilles}
        />

        <MultiSelect
          label="Tous les ingénieurs"
          icon={User}
          options={ingenieurOptions}
          selected={filterIngenieurs}
          onChange={setFilterIngenieurs}
          renderOption={(option) => (
            <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
              <div style={{
                width: '22px', height: '22px', borderRadius: '50%',
                background: option.couleur, color: 'white',
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                fontSize: '0.5625rem', fontWeight: 600, flexShrink: 0
              }}>
                {option.initiales}
              </div>
              <div>
                <span style={{ fontSize: '0.875rem', color: 'var(--gray-900)', display: 'block' }}>{option.label}</span>
                <span style={{ fontSize: '0.6875rem', color: 'var(--gray-500)' }}>{option.role}</span>
              </div>
            </div>
          )}
        />

        {activeFiltersCount > 0 && (
          <button
            onClick={clearAllFilters}
            style={{
              display: 'flex', alignItems: 'center', gap: '0.375rem',
              padding: '0.5rem 0.75rem', background: 'var(--gray-100)',
              border: 'none', borderRadius: 'var(--radius)', cursor: 'pointer',
              fontSize: '0.8125rem', color: 'var(--gray-600)', fontFamily: 'var(--font-sans)',
              transition: 'all 0.15s'
            }}
          >
            <X size={14} /> Réinitialiser ({activeFiltersCount})
          </button>
        )}
      </div>

      {/* Résultat filtrage */}
      {(activeFiltersCount > 0 || search) && (
        <div style={{
          display: 'flex', alignItems: 'center', gap: '0.5rem',
          marginBottom: '1rem', fontSize: '0.875rem', color: 'var(--gray-600)'
        }}>
          <TrendingUp size={16} />
          <span>
            <strong>{filteredAffaires.length}</strong> affaire{filteredAffaires.length > 1 ? 's' : ''} trouvée{filteredAffaires.length > 1 ? 's' : ''}
          </span>
          {filteredAffaires.length > 0 && (
            <span style={{ color: 'var(--primary)', fontWeight: 600 }}>
              — Total : {formatMontant(filteredAffaires.reduce((acc, a) => acc + a.montantHT, 0))}
            </span>
          )}
        </div>
      )}

      {/* Liste des affaires */}
      <div>
        {filteredAffaires.map(a => renderAffaireRow(a))}
      </div>

      {filteredAffaires.length === 0 && (
        <div className="empty-state">
          <Briefcase size={48} />
          <h3>Aucune affaire trouvée</h3>
          <p>Modifiez vos filtres de recherche.</p>
        </div>
      )}
    </div>
  );
};

export default AffairesList;
